import { useMutation, UseMutationResult, useQuery, useQueryClient, useSuspenseQuery, UseSuspenseQueryResult } from '@tanstack/react-query'
import { GetOutboundSchedulesReq, GetOverseasPriorityRes, SetOverseasPriorityReq } from './types'
import {
  bulkUpdateCutOffScheduleStatus,
  createCutOffSchedule,
  deleteCutOffSchedule,
  getOutboundSchedules,
  getOverseasPriority,
  setAllocationPriority,
  setOverseasPriority,
  updateCutOffScheduleStatus,
} from './apis'
import { ScheduleMenuCode } from '@/utils/menuCodeHeaders'

export const outboundSchedulesQueryKeys = {
  all: ['OUTBOUND_SCHEDULES'] as const,
  schedules: () => [...outboundSchedulesQueryKeys.all, 'SCHEDULES'] as const,
  schedule: (params: GetOutboundSchedulesReq) => [...outboundSchedulesQueryKeys.schedules(), params] as const,
  overseasPriority: () => [...outboundSchedulesQueryKeys.all, 'OVERSEAS_PRIORITY'] as const,
}

/** 일자별 출고 일정 조회 */
export const useGetOutboundSchedules = (params: GetOutboundSchedulesReq) => {
  const { data, ...rest } = useQuery({
    queryKey: outboundSchedulesQueryKeys.schedule(params),
    queryFn: () => getOutboundSchedules(params),
    select: (res) => res.data,
  })

  return { data, ...rest }
}

/** 컷오프 일정 생성 (현재 사용 X) */
export const useCreateCutOffSchedule = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createCutOffSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: outboundSchedulesQueryKeys.schedules() })
    },
  })
}

/** 컷오프 일정 삭제 (현재 사용 X) */
export const useDeleteCutOffSchedule = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteCutOffSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: outboundSchedulesQueryKeys.schedules() })
    },
  })
}

/** 컷오프 일정 상태 업데이트 */
export const useUpdateCutOffScheduleStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateCutOffScheduleStatus,
    onSuccess: (_, req) => {
      const { date, shipperCode } = req
      queryClient.invalidateQueries({ queryKey: outboundSchedulesQueryKeys.schedule({ date, shipperCode }) })
    },
  })
}

/** 컷오프 일정 일괄 상태 업데이트 */
export const useBulkUpdateCutOffScheduleStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: bulkUpdateCutOffScheduleStatus,
    onSuccess: (_, req) => {
      const { date, shipperCode } = req
      queryClient.invalidateQueries({ queryKey: outboundSchedulesQueryKeys.schedule({ date, shipperCode }) })
    },
  })
}

/** 할당 우선순위 설정 */
export const useSetAllocationPriority = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: setAllocationPriority,
    onSuccess: (_, req) => {
      const { date, shipperCode } = req
      queryClient.invalidateQueries({ queryKey: outboundSchedulesQueryKeys.schedule({ date, shipperCode }) })
    },
  })
}

/** 해외 출고 우선순위 조회 (Suspense) */
export const useSuspenseGetOverseasPriority = (menuCode: ScheduleMenuCode): UseSuspenseQueryResult<GetOverseasPriorityRes, Error> => {
  const { data, ...rest } = useSuspenseQuery({
    queryKey: outboundSchedulesQueryKeys.overseasPriority(),
    queryFn: () => getOverseasPriority(menuCode),
    select: (res) => res.data,
  })

  return { data, ...rest }
}

/** 해외 출고 우선순위 설정 */
export const useSetOverseasPriority = (menuCode: ScheduleMenuCode): UseMutationResult<unknown, Error, SetOverseasPriorityReq> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: SetOverseasPriorityReq) => setOverseasPriority(params, menuCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: outboundSchedulesQueryKeys.overseasPriority() })
    },
  })
}
